import axios from 'axios';
import { AttendanceRecordApiResponse, BatchApiResponse } from '../components/AttendanceManagement';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  withCredentials: true, // Include cookies for authentication
});

// Add request interceptor to handle authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Redirect to dashboard on access denied so locked pages bounce back to a safe area
    if (error?.response?.status === 403) {
      window.location.assign('/dashboard');
      return Promise.reject(error);
    }
    // Don't automatically redirect on 401 - let components handle auth state
    return Promise.reject(error);
  }
);

export interface Program {
  program_id: number;
  program_code: string;
  name: string;
  description?: string;
  pre_assessment_duration_minutes: number;
  post_assessment_duration_minutes: number;
  domain_training_days: number;
  ojt_days: number;
  launchpad_days: number;
  basecamp_days: number;
  status: 'Active' | 'Inactive';
  created_at: string;
}

export interface CreateProgram {
  name: string,
  program_code: string;
  description?: string,
  pre_assessment_duration_minutes: number;
  post_assessment_duration_minutes: number;
  domain_training_days: number;
  ojt_days: number;
  launchpad_days: number;
  basecamp_days: number;
  status?: 'Active' | 'Inactive'
}


export interface User {
  user_id: string;
  full_name: string;
  email: string
  role: string;
  phone: string;
  mp_id?: number,
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at?: string;
  mobilisation_partner?: any;
}

export interface CreateUserRequest {
  full_name: string;
  email: string;
  role: string;
  phone: string;
  mp_id?: number;
}

export interface UpdateUserRequest {
  full_name?: string;
  email?: string;
  role?: string;
  phone?: string;
  is_active?: boolean;
  mp_id?: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: User;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type TraineeLearningPathScheduleRange = {
  dates: string[];
  start: string | null;
  end: string | null;
};

export type TraineeLearningPathData = {
  trainee_id: number;
  batch: {
    batch_id: number;
    batch_code: string;
    status: string;
    program_name: string | null;
  };
  schedule: {
    launchpad: TraineeLearningPathScheduleRange;
    basecamp: TraineeLearningPathScheduleRange;
    domain: TraineeLearningPathScheduleRange;
    ojt: TraineeLearningPathScheduleRange;
    pre_assessment: { start: string | null; end: string | null };
    post_assessment: { start: string | null; end: string | null };
  };
  progress: Record<string, any> | null;
  knowlens?: {
    status?: string | number | null;
    message?: string | null;
    error?: string | null;
  };
};

export type TraineeLearningPathResponse = ApiResponse<TraineeLearningPathData>;

export type InquiryStatus = "Open" | "InProgress" | "Resolved" | "Closed";

export interface InquiryMessage {
  message_id: number;
  inquiry_id: number;
  sender_id?: string | null;
  sender_role?: string | null;
  message_type: "MESSAGE" | "STATUS_CHANGE";
  body: string;
  created_at: string;
  sender?: {
    user_id: string;
    full_name: string | null;
    email: string | null;
    role: string | null;
  } | null;
}

export const lmsApi = {
  getTraineeLoginUrl: async (): Promise<ApiResponse<{ redirectUrl: string }>> => {
    const response = await api.post<ApiResponse<{ redirectUrl: string }>>(
      "/assessments/knowlens/login"
    );
    return response.data;
  },
  getTraineeProgress: async (): Promise<TraineeLearningPathResponse> => {
    const response = await api.get<TraineeLearningPathResponse>(
      "/assessments/knowlens/progress"
    );
    return response.data;
  },
};

export interface InquirySummary {
  inquiry_id: number;
  trainee: {
    trainee_id: number;
    user_id: string;
    full_name: string | null;
    email: string | null;
  } | null;
  status: InquiryStatus;
  batch: {
    batch_id: number;
    batch_code: string | null;
    program_name: string | null;
    program_code: string | null;
  } | null;
  mp_partner: {
    mp_id: number;
    name: string | null;
  } | null;
  center_admin: {
    user_id: string;
    full_name: string | null;
    email: string | null;
  } | null;
  last_message_at: string;
  created_at: string;
  updated_at?: string | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  latest_message?: InquiryMessage | null;
}

export interface InquiryDetail extends InquirySummary {
  messages: InquiryMessage[];
}

export interface OjtCriterionRating {
  score: number;
  label: string;
  description: string;
}

export interface OjtCriterionDefinition {
  code: number;
  title: string;
  focus: string;
  ratingGuide: OjtCriterionRating[];
}

export interface OjtScoreBand {
  min: number;
  max: number;
  label: string;
  description: string;
}

export interface OjtScorecardMeta {
  template_version: string;
  instructions: string[];
  criteria: OjtCriterionDefinition[];
  score_bands: OjtScoreBand[];
  score_config: {
    min_score: number;
    max_score: number;
    criteria_count: number;
    max_total: number;
  };
}

export interface OjtBatchSummary {
  batch_id: number;
  batch_code: string;
  status: string;
  program_name: string | null;
  trainer_name: string | null;
  trainer_email: string | null;
  location: string | null;
  total_trainees: number;
  evaluated_trainees: number;
  last_uploaded_at: string | null;
}

export interface OjtBatchTraineeScore {
  code: number;
  title: string;
  score: number | null;
}

export interface OjtBatchTraineeRecord {
  trainee_id: number;
  trainee_name: string;
  login_id: string;
  evaluation_status: "pending" | "incomplete" | "complete";
  total_score: number | null;
  overall_band: string | null;
  citation: string | null;
  uploaded_at: string | null;
  uploaded_by: {
    user_id: string;
    full_name: string | null;
    email: string | null;
  } | null;
  scores: OjtBatchTraineeScore[];
}

export interface OjtBatchDetail {
  batch: {
    id: number;
    code: string;
    status: string;
    program_name: string | null;
    location: string | null;
    trainer_name: string | null;
    trainer_email: string | null;
    total_trainees: number;
    evaluated_trainees: number;
    last_uploaded_at: string | null;
  };
  trainees: OjtBatchTraineeRecord[];
  can_upload: boolean;
}

export interface OjtUploadError {
  row: number | "sheet";
  column?: string;
  message: string;
}
export const authApi = {
  // Login
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },

  // Logout
  logout: async (): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/auth/logout');
    return response.data;
  },

  // Verify token
  verifyToken: async (): Promise<{ valid: boolean; user?: User }> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  requestPasswordReset: async (email: string): Promise<ApiResponse> => {
    const response = await api.post('/auth/request-password-reset', { email });
    return response.data;
  },

  resetPasswordWithToken: async (token: string, new_password: string): Promise<ApiResponse> => {
    const response = await api.post('/auth/reset-password-with-token', { token, new_password });
    return response.data;
  }
};

export const userApi = {
  // Get all users with pagination and filtering
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    queryRole?: string;
    queryStatus?: string;
    queryMpId?: number;
  }): Promise<ApiResponse<User[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.queryRole) queryParams.append('queryRole', params.queryRole);
    if (params?.queryStatus) queryParams.append('queryStatus', params.queryStatus);
    if (params?.queryMpId) queryParams.append('queryMpId', params.queryMpId.toString());

    const response = await api.get<ApiResponse<User[]>>(`/users?${queryParams.toString()}`);
    return response.data;
  },

  // Get user by ID
  getUser: async (user_id: string): Promise<ApiResponse<User>> => {
    const response = await api.get<ApiResponse<User>>(`/users/${user_id}`);
    return response.data;
  },

  // Create user
  createUser: async (userData: CreateUserRequest): Promise<ApiResponse<User>> => {
    const response = await api.post<ApiResponse<User>>('/users', userData);
    return response.data;
  },

  // Update user
  updateUser: async (user_id: string, userData: UpdateUserRequest): Promise<ApiResponse<User>> => {
    const response = await api.put<ApiResponse<User>>(`/users/${user_id}`, userData);
    return response.data;
  },

  // Delete user
  inActiveUser: async (user_id: string): Promise<ApiResponse> => {
    const response = await api.put<ApiResponse>(`/users/inactive`, {user_id});
    return response.data;
  },
};

// Mobilisation API Types
export interface Mobilisation {
  mobilisation_id: number;
  title: string;
  description?: string;
  state: string;
  district: string;
  city: string;
  placement_company: string;
  placement_job_role: string;
  expected_salary: number;
  placement_type: string;
  placement_company_2?: string | null;
  placement_job_role_2?: string | null;
  expected_salary_2?: number | null;
  placement_type_2?: string | null;
  placement_company_3?: string | null;
  placement_job_role_3?: string | null;
  expected_salary_3?: number | null;
  placement_type_3?: string | null;
  placement_company_4?: string | null;
  placement_job_role_4?: string | null;
  expected_salary_4?: number | null;
  placement_type_4?: string | null;
  placement_company_5?: string | null;
  placement_job_role_5?: string | null;
  expected_salary_5?: number | null;
  placement_type_5?: string | null;
  candidates_count: number;
  proposed_batch_launch_date: string;
  proposed_placement_drive_date: string;
  status: string;
  revision_count: number;
  max_revisions: number;
  registration_start_date?: string;
  registration_end_date?: string;
  created_at: string;
  created_by?: string;
  mp_id: number;
  program_id: number;
  center_admin_id: string;
  local_trainer_identified: string;
  center_admin: {
    full_name: string;
    email: string;
    phone: string;
    mp_id: number;
  };
  program: {
    name: string;
  };
  approval_history?: { action: string; comments?: string; reviewed_at: string }[];
  trainee_registrations?: Trainee[];
}

export interface ValidRegistrationMobilisation extends Mobilisation {
  effective_registration_end_at: string;
  effective_registration_end_display?: string;
  effective_cutoff_basis: 'REGISTRATION_WINDOW' | 'LAUNCHPAD_LAST_DAY';
  launchpad_last_day_date?: string;
}

export interface CreateMobilisationRequest {
  title: string;
  description?: string;
  state: string;
  district: string;
  city: string;
  placement_company: string;
  placement_job_role: string;
  expected_salary: number;
  placement_type: string;
  placement_company_2?: string | null;
  placement_job_role_2?: string | null;
  expected_salary_2?: number | null;
  placement_type_2?: string | null;
  placement_company_3?: string | null;
  placement_job_role_3?: string | null;
  expected_salary_3?: number | null;
  placement_type_3?: string | null;
  placement_company_4?: string | null;
  placement_job_role_4?: string | null;
  expected_salary_4?: number | null;
  placement_type_4?: string | null;
  placement_company_5?: string | null;
  placement_job_role_5?: string | null;
  expected_salary_5?: number | null;
  placement_type_5?: string | null;
  candidates_count: number;
  proposed_batch_launch_date: string;
  proposed_placement_drive_date: string;
  rejection_reason?: string;
  center_admin_id: string;
  program_id: number;
  local_trainer_identified: string;
  mobilisation_id?: number;
}

// export interface Center {
//   center_id: number;
//   name: string;
//   address: string;
//   state: string;
//   district: string;
//   city: string;
//   pincode: string;
//   capacity: number;
//   classrooms?: number;
//   computer_labs?: number;
//   facilities?: string[];
//   mp_id: number;
//   status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected';
//   rejection_reason?: string;
//   created_at: string;
//   updated_at?: string;
//   mobilisation_partner: any[];
//   approval_history: any[];
// }


// export interface CreateCenterRequest {
//   name: string;
//   address: string;
//   city: string;
//   state: string;
//   district: string;
//   pincode: string;
//   classrooms?: number;
//   computer_labs?: number;
//   capacity: number;
//   facilities: string[];
// }

// Trainer API Types



export interface Center {
  center_id: number;
  name: string;
  address: string;
  nearbyLandmark: string;
  pincode: string;
  distanceFromCityCenter: 'ZERO_KM' | 'ONE_TO_FIVE_KM' | 'SIX_TO_TEN_KM' | 'ELEVEN_TO_TWENTY_KM' | 'BEYOND_TWENTY_KM';
  geoCoordinates?: string;
  cctvFeedLink?: string;

  // General Infrastructure Checklist
  general_cleanPremises?: boolean;
  general_accessible?: boolean;
  general_properCarpetArea?: boolean;
  general_separateAreas?: boolean;
  general_receptionDesk?: boolean;
  general_photographs?: string[];

  // Classroom Facilities Checklist
  classroom_dedicatedClassrooms?: boolean;
  classroom_whiteboard?: boolean;
  classroom_adequateChairs?: boolean;
  classroom_trainerTable?: boolean;
  classroom_lightingAndVentilation?: boolean;
  classroom_fansOrAC?: boolean;
  classroom_photographs?: string[];

  // IT Infrastructure Checklist
  it_projectorOrTv?: boolean;
  it_audioSystem?: boolean;
  it_internetSpeed?: boolean;
  it_upsBackup?: boolean;
  it_photographs?: string[];

  // Electrical and Safety Checklist
  electrical_properWiring?: boolean;
  electrical_fireExtinguisher?: boolean;
  electrical_emergencyExitPlan?: boolean;
  electrical_firstAidKit?: boolean;
  electrical_photographs?: string[];

  // Washrooms and Hygiene Checklist
  washrooms_separateToilets?: boolean;
  washrooms_regularCleaning?: boolean;
  washrooms_handwashing?: boolean;
  washrooms_photographs?: string[];

  // Record Keeping and Display Checklist
  recordKeeping_attendanceSystem?: boolean;
  recordKeeping_cctvInstalled?: boolean;
  recordKeeping_cctvLink?: string;
  recordKeeping_photographs?: string[];

  // Branding and Signage Checklist
  branding_brandingAtEntrance?: boolean;
  branding_posters?: boolean;
  branding_photographs?: string[];

  mp_id: number;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected';
  rejection_reason?: string;
  created_at: string;
  updated_at?: string;
  mobilisation_partner: any;
  approval_history: any[];
}

export interface CreateCenterRequest {
  name: string;
  address: string;
  nearbyLandmark?: string;
  pincode: string;
  distanceFromCityCenter: 'ZERO_KM' | 'ONE_TO_FIVE_KM' | 'SIX_TO_TEN_KM' | 'ELEVEN_TO_TWENTY_KM' | 'BEYOND_TWENTY_KM';
  geoCoordinates?: string;
  cctvFeedLink?: string;

  general_cleanPremises: boolean;
  general_accessible: boolean;
  general_properCarpetArea: boolean;
  general_separateAreas: boolean;
  general_receptionDesk: boolean;
  general_photographs?: string[];

  classroom_dedicatedClassrooms: boolean;
  classroom_whiteboard: boolean;
  classroom_adequateChairs: boolean;
  classroom_trainerTable: boolean;
  classroom_lightingAndVentilation: boolean;
  classroom_fansOrAC: boolean;
  classroom_photographs?: string[];

  it_projectorOrTv: boolean;
  it_audioSystem: boolean;
  it_internetSpeed: boolean;
  it_upsBackup: boolean;
  it_photographs?: string[];

  electrical_properWiring: boolean;
  electrical_fireExtinguisher: boolean;
  electrical_emergencyExitPlan: boolean;
  electrical_firstAidKit: boolean;
  electrical_photographs?: string[];

  washrooms_separateToilets: boolean;
  washrooms_regularCleaning: boolean;
  washrooms_handwashing: boolean;
  washrooms_photographs?: string[];

  recordKeeping_attendanceSystem: boolean;
  recordKeeping_cctvInstalled: boolean;
  recordKeeping_cctvLink?: string;
  recordKeeping_photographs?: string[];

  branding_brandingAtEntrance: boolean;
  branding_posters: boolean;
  branding_photographs?: string[];
}
export type TrainerStatus = 'Invited' | 'InProgress' | 'Completed';
export type TrainerTag = 'Trainer' | 'Master Trainer';

export type LanguageProficiencyLevel = 'BASIC' | 'INTERMEDIATE' | 'ADVANCED';
export type EducationLevel = 'GRADUATE' | 'POST_GRADUATE' | 'DOCTORATE';
export type GenderCode = 'MALE' | 'FEMALE' | 'OTHER';

export interface TrainerContact {
  full_name: string;
  email: string;
  phone: string;
}

export interface TrainerProfile {
  gender: GenderCode | null;
  date_of_birth: string | null;
  native_language: string | null;
  native_language_proficiency: LanguageProficiencyLevel | null;
  english_proficiency: LanguageProficiencyLevel | null;
  hindi_proficiency: LanguageProficiencyLevel | null;
  occupation: string | null;
  home_town: string | null;
  state: string | null;
  district?: string | null;
  city: string | null;
  address: string | null;
  pin_code: string | null;
  whatsapp_number: string | null;
  education_level: EducationLevel | null;
  graduation_stream: string | null;
  post_graduation_stream?: string | null;
  academic_qualifications: string | null;
  professional_qualifications: string | null;
  certificate_courses?: string | null;
  formal_facilitation_training?: boolean | null;
  training_institutes?: string | null;
  employer_name: string | null;
  industry: string | null;
  years_of_experience?: number | null;
  designation: string | null;
  freelance_trainer_age?: number | null;
  top_specializations: string | null;
  trained_college_students?: boolean | null;
  trained_entry_level_employees?: boolean | null;
  trained_corporate_leaders?: boolean | null;
  trained_ngo_beneficiaries?: boolean | null;
  trained_educational_institutes?: boolean | null;
  knowledge_updation: string | null;
  states_visited_count?: number | null;
  performing_arts_experience?: string | null;
  sports_experience?: string | null;
  reading_preferences?: string | null;
  social_work_details?: string | null;
  has_office?: boolean | null;
  owns_car?: boolean | null;
  owns_laptop?: boolean | null;
  has_high_speed_broadband?: boolean | null;
  has_online_training_experience?: boolean | null;
  can_travel_for_training?: boolean | null;
  availability_per_month: string | null;
}

export interface Trainer {
  trainer_id: number;
  mp_id: number;
  mp_name: string | null;
  trainer_tag: TrainerTag;
  status: TrainerStatus;
  program_id: number;
  program?: {
    program_id: number;
    name: string;
    status: 'Active' | 'Inactive';
  } | null;
  onboarding_started_at: string | null;
  onboarding_completed_at: string | null;
  is_available: boolean;
  contact: TrainerContact;
  profile: TrainerProfile | null;
  created_at: string;
  updated_at?: string | null;
}

export interface CreateTrainerRequest {
  full_name: string;
  email: string;
  phone: string;
  program_id: number;
  trainer_tag: TrainerTag;
}

export interface TrainerSelfOnboardingPayload {
  gender: GenderCode;
  date_of_birth: string;
  native_language: string;
  native_language_proficiency: LanguageProficiencyLevel;
  english_proficiency: LanguageProficiencyLevel;
  hindi_proficiency: LanguageProficiencyLevel;
  occupation: string;
  home_town: string;
  state: string;
  district?: string;
  city: string;
  address: string;
  pin_code: string;
  whatsapp_number: string;
  education_level: EducationLevel;
  graduation_stream: string;
  post_graduation_stream?: string;
  academic_qualifications: string;
  professional_qualifications: string;
  certificate_courses?: string;
  formal_facilitation_training: boolean;
  training_institutes?: string;
  employer_name: string;
  industry: string;
  years_of_experience: number;
  designation: string;
  freelance_trainer_age: number;
  top_specializations: string;
  trained_college_students: boolean;
  trained_entry_level_employees: boolean;
  trained_corporate_leaders: boolean;
  trained_ngo_beneficiaries: boolean;
  trained_educational_institutes: boolean;
  knowledge_updation: string;
  states_visited_count: number;
  performing_arts_experience?: string;
  sports_experience?: string;
  reading_preferences?: string;
  social_work_details?: string;
  has_office: boolean;
  owns_car: boolean;
  owns_laptop: boolean;
  has_high_speed_broadband: boolean;
  has_online_training_experience: boolean;
  can_travel_for_training: boolean;
  availability_per_month: string;
}

// Trainee API Types
export interface Trainee {
  trainee_id: number;
  full_name: string;
  email?: string;
  phone: string;
  date_of_birth: string;
  gender: 'Male' | 'Female' | 'Other';
  address: string;
  city: string;
  state: string;
  pincode: string;
  education_level: string;
  previous_experience?: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  aadhaar_number?: string;
  center_id: number;
  status: 'Draft' | 'Active' | 'Inactive' | 'Pending';
  is_provisional: boolean;
  created_at: string;
  updated_at?: string;
  center?: any;
}

export interface CreateTraineeRequest {
  full_name: string;
  email?: string;
  phone: string;
  date_of_birth: string;
  gender: 'Male' | 'Female' | 'Other';
  address: string;
  city: string;
  state: string;
  pincode: string;
  education_level: string;
  previous_experience?: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  aadhaar_number?: string;
  center_id: number;
  is_provisional?: boolean;
}

export interface CreateTraineesBulkRequest {
  mobilisation_id: number;
  file: File | null
}


export interface BatchFlags {
  has_enrollment: boolean;
  pre_any_taken: boolean;
  ca_pre_edits_left: number;
  ca_can_edit_pre_now: boolean;
  can_customer_approve: boolean;
  can_pick_final_list_of_trainees: boolean;
  part_two_open: boolean;
  change_request_window_open: boolean;
  change_request_window_type?: 'pre-launch' | 'post-launch' | null;
  change_request_post_launch?: boolean;
  pending_change_request: boolean;
}

export interface BatchChangeRequestSummary {
  id?: number;
  status: 'Pending' | 'Approved' | 'Rejected';
  requested_at: string;
  requested_by: string;
  note?: string | null;
  requested_payload?: Record<string, any> | null;
  review_comment?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  window_type?: 'pre-launch' | 'post-launch';
}

export interface BatchChangeRequestStats {
  approved_count: number;
  remaining: number | null;
  pending: boolean;
  limit?: number | null;
}

export type BatchStatus = 'Draft' | 'InProgress' | 'Completed' | 'Cancelled';
export type BatchProposalStatus = 'Draft' | 'Submitted' | 'Approved' | 'Rejected' | 'Cancelled';

// Batch
export interface Batch {
  batch_id: number;
  mobilisation_id: number;
  center_id: number;
  trainer_id: number;
  batch_code: string;
  program_id: number;

  status: BatchStatus;
  batch_proposal_status: BatchProposalStatus;
  is_center_infra_ready: boolean;
  is_recruiter_engaged: boolean;
  consent_sent: boolean;

  // ---- Launchpad (date-only strings "YYYY-MM-DD")
  launchpad_session_dates: string[];

  // ---- Pre assessment (ISO datetimes)
  pre_assessment_start_datetime_planned: string;   // ISO datetime
  pre_assessment_start_datetime_actual: string;    // ISO datetime
  pre_assessment_end_datetime: string;             // ISO datetime
  ca_pre_schedule_edit_count: number;

  // ---- Post assessment (ISO datetimes)
  post_assessment_datetime_planned: string;        // ISO datetime
  post_assessment_datetime_actual: string;         // ISO datetime
  post_assessment_end_datetime: string;            // ISO datetime

  // ---- Batch launch / placement drive (date-only; will serialize as ISO at 00:00:00Z)
  batch_launch_date_planned: string;               // date-only
  batch_launch_date_actual: string;                // date-only
  placement_drive_date_planned: string;            // date-only
  placement_drive_date_actual: string;             // date-only

  // ---- Segment date arrays (date-only strings "YYYY-MM-DD")
  basecamp_dates_planned: string[];
  basecamp_dates_actual: string[];
  domain_training_dates_planned: string[];
  domain_training_dates_actual: string[];
  ojt_dates_planned: string[];
  ojt_dates_actual: string[];

  // ---- Proposal workflow
  batch_proposal_approval_date?: string | null;    // date-only
  batch_proposal_rejection_reason?: string | null;

  // ---- Audit/meta
  metadata: any;
  created_at: string;                               // ISO datetime
  updated_at: string | null;                        // ISO datetime
  created_by: string;
  updated_by: string | null;

  // ---- Joined / denormalized for UI (if your API includes them)
  mobilisation_name?: string;
  assignedTrainerName?: string;
  assignedCenterName?: string;
  batch_trainees?: any[];
  trainees?: any[];
  mobilisation?: any;
  registration_detials?: any[];
  center_admin?: User;
  program?: Program;
  approval_history?: { action: string; comments: string; reviewed_at: string }[];

  // ---- Convenience/computed (often included by your API)
  pre_assessment_start_datetime_effective?: string; // actual ?? planned
  post_assessment_datetime_effective?: string;      // actual ?? planned
  change_request?: BatchChangeRequestSummary | null;
  change_request_stats?: BatchChangeRequestStats | null;
  flags?: BatchFlags | null;
}



export interface BatchFormData {
  mobilisation_id: number;
  center_id: number;
  trainer_id: number;
  is_center_infra_ready: boolean;
  is_recruiter_engaged: boolean;

  launchpad_session_dates: string[]; // date[]
  basecamp_dates: string[];          // date[]
  domain_training_dates: string[];   // date[]
  ojt_dates: string[];               // date[]

  pre_assessment_start_datetime: string; // datetime-local
  post_assessment_datetime: string;      // datetime-local

  batch_launch_date: string;        // date-only
  placement_drive_date: string;     // date-only

  trainees: Array<{ id: number; name: string; email?: string }>;
  consentSent?: boolean;
  metadata?: any;
  created_by?: string;
  updated_by?: string | null;
}

export interface RegisterdTrainee {
  full_name: string;
  email: string;
  phone: string;
  registration_id: string; // BigInt as string from API/JSON
  mobilisation_id: number;
  trainee_id: number;
  registration_date: string; // ISO
  source?: string | null;
  documents_verified: boolean;
  eligibility_checked: boolean;
  is_active: boolean;
  selected_for_batch: boolean;
  metadata: any; // JSON
  // Trainee fields
  gender?: string | null;
  date_of_birth?: string | null;
  highest_education?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
}


// Define types for the fetched data
export interface TraineeUser {
  full_name: string;
  email: string;
  phone: string;
}

export interface TraineeData {
  user: TraineeUser;
  trainee_id: number;
  date_of_birth: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  address: string;
  city: string;
  state: string;
  pincode: string;
  highest_education: string;
  center_id: number;
  created_at: string;
  updated_at?: string;
}

export interface TraineeRegistrationData {
  registration_id: number;
  registration_date: string;
  source: string;
  consent_mail_sent?: string;
  consent_given?: boolean
  consent_timestamp?: string;
  trainee: TraineeData;
}

export interface TraineesRegisteredData {
  mobilisation_id: number;
  registration_start_date?: string;
  registration_end_date?: string;
  effective_registration_end_at?: string | null;
  effective_registration_end_display?: string | null;
  effective_cutoff_basis?: 'REGISTRATION_WINDOW' | 'LAUNCHPAD_LAST_DAY' | null;
  launchpad_last_day_date?: string;
  title: string;
  description: string;
  state: string;
  district: string;
  city: string;
  program: { name: string };
  center_admin: any;
  mobilisation_partner?: { name: string }; // Optional for SUPER_ADMIN
  trainee_registrations: TraineeRegistrationData[];
}

// Updated TraineeConsentStatus to reflect individual consent status
export interface TraineeConsentStatus {
  trainee_id: number; // Corresponds to trainee_id
  full_name: string; // Corresponds to user.full_name
  email?: string; // Corresponds to user.email
  status: 'pending' | 'sending' | 'sent' | 'failed';
  error?: string;
}

// API Services

export const programsApi = {
  getPrograms: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    queryRole?: string;
    queryStatus?: string;
    queryMpId?: number;
  }): Promise<ApiResponse<Program[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.queryRole) queryParams.append('queryRole', params.queryRole);
    if (params?.queryStatus) queryParams.append('queryStatus', params.queryStatus);
    if (params?.queryMpId) queryParams.append('queryMpId', params.queryMpId.toString());

    const qs = queryParams.toString();
    const url = qs ? '/programs?' + qs : '/programs';
    const response = await api.get<ApiResponse<Program[]>>(url);
    return response.data;
  },

  getProgram: async (program_id: number): Promise<ApiResponse<Program>> => {
    const response = await api.get<ApiResponse<Program>>('/programs/' + program_id);
    return response.data;
  },

  createProgram: async (programData: CreateProgram): Promise<ApiResponse<Program>> => {
    const response = await api.post<ApiResponse<Program>>('/programs', programData);
    return response.data;
  },

  updateProgram: async (program_id: string, programData: Partial<CreateProgram>): Promise<ApiResponse<Program>> => {
    const response = await api.put<ApiResponse<Program>>('/programs/' + program_id, programData);
    return response.data;
  },

  deleteProgram: async (program_id: string): Promise<ApiResponse> => {
    const response = await api.delete<ApiResponse>('/programs/' + program_id);
    return response.data;
  },
};

export const mobilisationApi = {
  // Get all mobilisations
  getMobilisations: async (params?: {
    page?: number;
    limit?: number;
    queryStatus?: string;
    mp_id?: number;
  }): Promise<ApiResponse<Mobilisation[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.queryStatus) queryParams.append('queryStatus', params.queryStatus);
    if (params?.mp_id) queryParams.append('mp_id', params.mp_id.toString());

    const response = await api.get<ApiResponse<Mobilisation[]>>(`/mobilisations?${queryParams.toString()}`);
    return response.data;
  },

  // Get mobilisations for batch proposal 
  getBPMobilisation: async (): Promise<ApiResponse<Mobilisation[]>> => {
    const response = await api.get<ApiResponse<Mobilisation[]>>(`/mobilisations/batch-prop`);
    return response.data;
  },

  // Get mobilisation by ID
  getMobilisation: async (mobilisation_id: string): Promise<ApiResponse<Mobilisation>> => {
    const response = await api.get<ApiResponse<Mobilisation>>(`/mobilisations/${mobilisation_id}`);
    return response.data;
  },

  // Create mobilisation
  createMobilisation: async (data: CreateMobilisationRequest): Promise<ApiResponse<Mobilisation>> => {
    const response = await api.post<ApiResponse<Mobilisation>>('/mobilisations', data);
    return response.data;
  },

  // Update mobilisation
  updateMobilisation: async (mobilisation_id: string, data: Partial<CreateMobilisationRequest>): Promise<ApiResponse<Mobilisation>> => {
    const response = await api.put<ApiResponse<Mobilisation>>(`/mobilisations/${mobilisation_id}`, data);
    return response.data;
  },

  // Submit mobilisation for approval
  submitMobilisation: async (mobilisation_id: string): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/mobilisations/${mobilisation_id}/submit`);
    return response.data;
  },

  // Review mobilisation
  reviewMobilisation: async (mobilisation_id: string, data: {
    action: 'approve' | 'reject' | 'request_revision';
    comments?: string;
  }): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/mobilisations/${mobilisation_id}/review`, data);
    return response.data;
  },

  // Delete mobilisation
  deleteMobilisation: async (mobilisation_id: string): Promise<ApiResponse> => {
    const response = await api.delete<ApiResponse>(`/mobilisations/${mobilisation_id}`);
    return response.data;
  },
};

export const centerApi = {
  // Get all centers
  getCenters: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    mp_id?: number;
  }): Promise<ApiResponse<Center[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.mp_id) queryParams.append('mp_id', params.mp_id.toString());

    const response = await api.get<ApiResponse<Center[]>>(`/centers?${queryParams.toString()}`);
    return response.data;
  },

  // Get center by ID
  getCenter: async (center_id: number): Promise<ApiResponse<Center>> => {
    const response = await api.get<ApiResponse<Center>>(`/centers/${center_id}`);
    return response.data;
  },

  // Get centers for batch proposal
  getBPCenters: async (): Promise<ApiResponse<Center[]>> => {
    const response = await api.get<ApiResponse<Center[]>>(`/centers/batch-prop`);
    return response.data;
  },


  // Create center
  createCenter: async (data: CreateCenterRequest): Promise<ApiResponse<Center>> => {
    const response = await api.post<ApiResponse<Center>>('/centers', data);
    return response.data;
  },

  // Update center
  updateCenter: async (center_id: number, data: Partial<CreateCenterRequest>): Promise<ApiResponse<Center>> => {
    const response = await api.put<ApiResponse<Center>>(`/centers/${center_id}`, data);
    return response.data;
  },

  // Submit batch for approval
  submitCenter: async (center_id: number): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/centers/${center_id}/submit`);
    return response.data;
  },

  // Review center
  reviewCenter: async (center_id: number, data: {
    action: 'Approved' | 'Rejected';
    comments?: string;
  }): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/centers/${center_id}/review`, data);
    return response.data;
  },

  // Delete center
  deleteCenter: async (center_id: number): Promise<ApiResponse> => {
    const response = await api.delete<ApiResponse>(`/centers/${center_id}`);
    return response.data;
  },
};

export const trainerApi = {
  // Get all trainers
  getTrainers: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    mp_id?: number;
    trainer_tag?: TrainerTag;
  }): Promise<ApiResponse<Trainer[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.mp_id) queryParams.append('mp_id', params.mp_id.toString());
    if (params?.trainer_tag) queryParams.append('trainer_tag', params.trainer_tag);

    const response = await api.get<ApiResponse<Trainer[]>>(`/trainers?${queryParams.toString()}`);
    return response.data;
  },

  // Get trainers for batch proposal
  getBPTrainers: async (): Promise<ApiResponse<Trainer[]>> => {
    const response = await api.get<ApiResponse<Trainer[]>>(`/trainers/batch-prop`);
    return response.data;
  },


  // Create trainer
  createTrainer: async (data: CreateTrainerRequest & { resend?: boolean }): Promise<ApiResponse<Trainer>> => {
    const response = await api.post<ApiResponse<Trainer>>('/trainers', data);
    return response.data;
  },

  resendTrainerInvite: async (trainer_id: string | number): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/trainers/${trainer_id}/resend-invite`);
    return response.data;
  },

  // Update trainer
  updateTrainer: async (trainer_id: string, data: Partial<CreateTrainerRequest>): Promise<ApiResponse<Trainer>> => {
    const response = await api.put<ApiResponse<Trainer>>(`/trainers/${trainer_id}`, data);
    return response.data;
  },

  // Self onboarding profile
  getSelfProfile: async (): Promise<ApiResponse<Trainer>> => {
    const response = await api.get<ApiResponse<Trainer>>('/trainers/self-profile');
    return response.data;
  },

  startSelfOnboarding: async (): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/trainers/self-profile/start');
    return response.data;
  },

  completeSelfOnboarding: async (data: TrainerSelfOnboardingPayload): Promise<ApiResponse<Trainer>> => {
    const response = await api.post<ApiResponse<Trainer>>('/trainers/self-profile/complete', data);
    return response.data;
  },

  // Delete trainer
  deleteTrainer: async (trainer_id: string): Promise<ApiResponse> => {
    const response = await api.delete<ApiResponse>(`/trainers/${trainer_id}`);
    return response.data;
  },
};

export const traineeApi = {
  // Get all trainees
  getTrainees: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    center_id?: number;
    is_provisional?: boolean;
  }): Promise<ApiResponse<Trainee[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.center_id) queryParams.append('center_id', params.center_id.toString());
    if (params?.is_provisional !== undefined) queryParams.append('is_provisional', params.is_provisional.toString());

    const response = await api.get<ApiResponse<Trainee[]>>(`/trainee-registration?${queryParams.toString()}`);
    return response.data;
  },

  // Get trainee by ID
  getTrainee: async (trainee_id: string): Promise<ApiResponse<Trainee>> => {
    const response = await api.get<ApiResponse<Trainee>>(`/trainee-registration/${trainee_id}`);
    return response.data;
  },

  // Create trainee
  createTrainee: async (data: CreateTraineeRequest): Promise<ApiResponse<Trainee>> => {
    const response = await api.post<ApiResponse<Trainee>>('/trainee-registration', data);
    return response.data;
  },

  // Bulk create trainees
  bulkCreateTrainees: async (data: { trainees: CreateTraineeRequest[] }): Promise<ApiResponse<{ created: number; errors: any[] }>> => {
    const response = await api.post<ApiResponse<{ created: number; errors: any[] }>>('/trainee-registration/bulk', data);
    return response.data;
  },

  // Update trainee
  updateTrainee: async (trainee_id: string, data: Partial<CreateTraineeRequest>): Promise<ApiResponse<Trainee>> => {
    const response = await api.put<ApiResponse<Trainee>>(`/trainee-registration/${trainee_id}`, data);
    return response.data;
  },

  // Deactivate trainee
  deactivateTrainee: async (trainee_id: string): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/trainee-registration/${trainee_id}/deactivate`);
    return response.data;
  },

  // Get trainee stats
  getTraineeStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/trainee-registration/stats');
    return response.data;
  },
};

export const traineeRegistrationApi = {
  // Get all trainees
  getTrainees: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    mobilisation_id: number;
  }): Promise<ApiResponse<TraineeRegistrationData[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get<ApiResponse<TraineeRegistrationData[]>>(`/mobilisations/${parseInt(params.mobilisation_id.toString())}/registrations?${queryParams.toString()}`);
    return response.data;
  },

  getTemplate: async (): Promise<Blob> => {
    const response = await api.get<Blob>('/mobilisations/registrations/template', {
      responseType: 'blob'  // ← tell Axios you want raw bytes
    });
    return response.data;
  },

  getValidMobilisations: async (): Promise<ApiResponse<ValidRegistrationMobilisation[]>> => {
    const response = await api.get<ApiResponse<ValidRegistrationMobilisation[]>>(`/mobilisations/registrations/valid-mobilisations`);
    return response.data;
  },

  // Bulk create trainees
  bulkCreateTrainees: async (data: CreateTraineesBulkRequest): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(
      '/mobilisations/registrations/bulk-upload',
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        responseType: 'blob'
      },
    )
    return response
  },

  sendConsent: async (traineeIds: number[], center_name: string, mobilisation_id: number): Promise<ApiResponse<TraineeConsentStatus[]>> => {
    const response = await api.put<ApiResponse<TraineeConsentStatus[]>>(`mobilisations/registrations/${mobilisation_id}/send-consent`, { traineeIds, center_name });
    return response.data;
  },

  getTraineeList: async (): Promise<ApiResponse<TraineesRegisteredData[]>> => {
    const response = await api.get<ApiResponse<TraineesRegisteredData[]>>(`/mobilisations/registrations/list-trainees`);
    return response.data;
  },

  // Get trainee by ID
  // getTrainee: async (trainee_id: string): Promise<ApiResponse<Trainee>> => {
  //   const response = await api.get<ApiResponse<Trainee>>(`/trainee-registration/registrations/list-trainees`);
  //   return response.data;
  // },

  // Create trainee
  createTrainee: async (data: CreateTraineeRequest): Promise<ApiResponse<Trainee>> => {
    const response = await api.post<ApiResponse<Trainee>>('/trainee-registration', data);
    return response.data;
  },

  updateTrainee: async (trainee_id: string, data: Partial<CreateTraineeRequest>): Promise<ApiResponse<Trainee>> => {
    const response = await api.put<ApiResponse<Trainee>>(`/trainee-registration/${trainee_id}`, data);
    return response.data;
  },

  // Bulk create trainees
  // bulkCreateTrainees: async (data: { trainees: CreateTraineeRequest[] }): Promise<ApiResponse<{ created: number; errors: any[] }>> => {
  //   const response = await api.post<ApiResponse<{ created: number; errors: any[] }>>('/trainee-registration/bulk', data);
  //   return response.data;
  // },

  // Update trainee


  // Deactivate trainee
  deactivateTrainee: async (trainee_id: string): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/trainee-registration/${trainee_id}/deactivate`);
    return response.data;
  },

  // Get trainee stats
  getTraineeStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/trainee-registration/stats');
    return response.data;
  },
};

export const batchApi = {
  getBatches: async (params?: {
    page?: number;
    limit?: number;
    pageSize?: number;
    status?: string;
    statusFilter?: string;
    center_id?: number;
    mobilisation_id?: string;
    userRole?: string;
    mpId?: number;
  }): Promise<ApiResponse<Batch[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize ?? params?.limit) {
      queryParams.append('pageSize', String(params?.pageSize ?? params?.limit));
    }
    if (params?.statusFilter ?? params?.status) {
      queryParams.append('statusFilter', String(params?.statusFilter ?? params?.status));
    }
    if (params?.center_id) queryParams.append('center_id', params.center_id.toString());
    if (params?.mobilisation_id) queryParams.append('mobilisation_id', params.mobilisation_id);
    if (params?.userRole) queryParams.append('userRole', params.userRole);
    if (params?.mpId) queryParams.append('mpId', params.mpId.toString());

    const qs = queryParams.toString();
    const response = await api.get<ApiResponse<Batch[]>>(qs ? `/batches/api/batches?${qs}` : '/batches/api/batches');
    return response.data;
  },

  getBatch: async (batch_id: string): Promise<ApiResponse<Batch>> => {
    const response = await api.get<ApiResponse<Batch>>(`/batches/api/batches/${batch_id}`);
    return response.data;
  },

  createBatch: async (data: BatchFormData): Promise<ApiResponse<Batch>> => {
    const response = await api.post<ApiResponse<Batch>>('/batches/api/batches', data);
    return response.data;
  },

  updateBatch: async (batch_id: string, data: Partial<BatchFormData>): Promise<ApiResponse<Batch>> => {
    const response = await api.put<ApiResponse<Batch>>(`/batches/api/batches/${batch_id}`, data);
    return response.data;
  },

  submitBatchProposal: async (batchId: number): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/batches/api/batches/${batchId}/submit-for-approval`);
    return response.data;
  },

  reviewBatchProposal: async (
    batchId: number,
    data: { action: 'Approved' | 'Rejected'; comments?: string }
  ): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/batches/api/batches/${batchId}/review`, data);
    return response.data;
  },

  cancelBatch: async (batchId: number): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/batches/api/batches/${batchId}/cancel`);
    return response.data;
  },

  createChangeRequest: async (batchId: number, payload: Record<string, any>): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/batches/api/batches/${batchId}/change-requests`, payload);
    return response.data;
  },

  reviewChangeRequest: async (
    batchId: number,
    requestId: number,
    data: { action: 'Approved' | 'Rejected'; comment?: string }
  ): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(
      `/batches/api/batches/${batchId}/change-requests/${requestId}/review`,
      data
    );
    return response.data;
  },

  updatePreSchedule: async (
    batchId: number,
    iso: string
  ): Promise<ApiResponse<{ start: string; end: string; duration_minutes: number; edits_left_for_CA: number }>> => {
    const response = await api.patch<
      ApiResponse<{ start: string; end: string; duration_minutes: number; edits_left_for_CA: number }>
    >(`/batches/api/batches/${batchId}/pre-schedule`, { start: iso });
    return response.data;
  },

  updateBatchLaunch: async (batchId: number, iso: string): Promise<ApiResponse> => {
    const response = await api.patch<ApiResponse>(`/batches/api/batches/${batchId}/launch`, { date: iso });
    return response.data;
  },
};

export interface StageComparison {
  planned: string | null;
  actual: string | null;
}

export interface StageArrayComparison {
  planned: string[];
  actual: string[];
  plannedStart: string | null;
  actualStart: string | null;
}

export interface PlannedVsActualRow {
  batchId: number;
  batchCode: string;
  status: string;
  cancelled: boolean;
  mobilisationTitle: string | null;
  partner: {
    mpId: number | null;
    name: string | null;
  };
  program: {
    programId: number;
    name: string | null;
  };
  preAssessment: StageComparison;
  postAssessment: StageComparison;
  batchLaunch: StageComparison;
  placementDrive: StageComparison;
  basecamp: StageArrayComparison;
  domainTraining: StageArrayComparison;
  ojt: StageArrayComparison;
}

export interface PlannedVsActualResponse {
  success: boolean;
  data: PlannedVsActualRow[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface PartnerPerformanceRow {
  mpId: number;
  partnerName: string;
  allocated: number;
  completed: number;
  completionRate: number;
  belowThreshold: boolean;
}

export interface PartnerPerformanceResponse {
  success: boolean;
  data: PartnerPerformanceRow[];
  meta: {
    threshold: number;
  };
}

export interface BatchEfficiencyRow {
  batchId: number;
  batchCode: string;
  status: string;
  partnerName: string | null;
  centerName: string | null;
  city: string | null;
  targetLaunchpadSize: number;
  targetBatchSize: number;
  actualLaunchpadSize: number;
  actualBatchSize: number;
  preAssessmentAssigned: number;
  preAssessmentCompleted: number;
  certifiedCount: number;
  avgPreScore: number | null;
  avgPostScore: number | null;
  placedCount: number;
}

export interface BatchEfficiencyResponse {
  success: boolean;
  data: BatchEfficiencyRow[];
}

export interface ExceptionRow {
  id: number;
  entity_type: string;
  entity_id: number | null;
  module: string;
  rule_key: string;
  detected_at: string;
  payload: any;
}

export interface ExceptionsResponse {
  success: boolean;
  data: ExceptionRow[];
}

export type KnowlensModule = {
  module_name?: string;
  module_progress?: string;
  module_score?: string;
};

export type KnowlensTraineeConsumption = {
  firstname?: string;
  lastname?: string;
  email?: string;
  modules?: KnowlensModule[];
};

export type BatchConsumptionResponse = {
  success: boolean;
  status?: string | number | null;
  metadata?: any;
  records?: KnowlensTraineeConsumption[] | null;
  data?: any;
  raw?: any;
};

export const reportsApi = {
  getBatchConsumption: async (payload: { batchCode: string }): Promise<BatchConsumptionResponse> => {
    const response = await api.post<BatchConsumptionResponse>('/reports/batch-consumption', payload);
    return response.data;
  },
  getPlannedVsActual: async (params?: {
    page?: number;
    pageSize?: number;
    mpId?: number;
    batchId?: number;
    batchCode?: string;
  }): Promise<PlannedVsActualResponse> => {
    const response = await api.get<PlannedVsActualResponse>('/reports/planned-vs-actual', {
      params,
    });
    return response.data;
  },
  getPartnerPerformance: async (params?: {
    partnerId?: number;
    partnerName?: string;
    centerId?: number;
    from?: string;
    to?: string;
    threshold?: number;
  }): Promise<PartnerPerformanceResponse> => {
    const response = await api.get<PartnerPerformanceResponse>('/reports/partner-performance', {
      params,
    });
    return response.data;
  },
  getBatchEfficiency: async (params: {
    mpId: number;
    statuses?: string;
  }): Promise<BatchEfficiencyResponse> => {
    const response = await api.get<BatchEfficiencyResponse>('/reports/batch-efficiency', {
      params,
    });
    return response.data;
  },
  exportBatchEfficiency: async (params: {
    mpId: number;
    statuses?: string;
  }): Promise<Blob> => {
    const response = await api.get('/reports/batch-efficiency/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  getExceptions: async (params?: {
    module?: string;
    rule?: string;
    entityType?: string;
    from?: string;
    to?: string;
  }): Promise<ExceptionsResponse> => {
    const response = await api.get<ExceptionsResponse>('/reports/exceptions', {
      params,
    });
    return response.data;
  },
};

export const attendanceApi = {

  getBatchAttendance: async (batch_id: string): Promise<ApiResponse<AttendanceRecordApiResponse[]>> => {
    const response = await api.get<ApiResponse<AttendanceRecordApiResponse[]>>(`/attendance/batch/${batch_id}`);
    return response.data;
  },

  getAllBatchesAttendance: async (): Promise<ApiResponse<BatchApiResponse[]>> => {
    const response = await api.get<ApiResponse<BatchApiResponse[]>>(`/attendance/batches`);
    return response.data;
  },

  getBatchSessions: async (batchId: number | string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/api/attendance/${batchId}/sessions`);
    return response.data
  },

  markAttendanceBulk: async (
    batchId: string,
    payload: {
      session_date: string;
      session_type: string;
      session_hours: number;
      items: Array<{ trainee_id: number; present: boolean; remarks?: string }>;
    }
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/api/attendance/${batchId}/mark`, payload);
    return response.data;
  },

  getBatchSummary: async (batchId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/api/attendance/${batchId}/summary`);
    return response.data;
  },
  exportBatchCsv: async (batchId: string): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(`/api/attendance/${batchId}/export`, { responseType: 'blob' });
    return response;
  },
};


export type Side = 'pre' | 'post';
export interface TraineeAssessmentsDTO {
  trainee_id: number;
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  batch_assessments: Array<{
    attendance_percentage: string; // or number if you convert
    is_pre_assessment_assigned: boolean;
    is_pre_assessment_taken: boolean;
    is_post_assessment_assigned: boolean;
    is_post_assessment_taken: boolean;
    certification_status: string | null;
    batch: {
      batch_id: number;
      batch_code: string;
      program: { program_id: number; name: string };

      pre_assessment_start_datetime: string | null;
      pre_assessment_end_datetime: string | null;

      post_assessment_datetime: string | null;
      post_assessment_end_datetime: string | null;
    };
    computed: {
      pre: { scheduledAt?: string | null; open: boolean; locked: boolean; taken: boolean; assigned: boolean };
      post: { scheduledAt?: string | null; open: boolean; locked: boolean; taken: boolean; assigned: boolean };
    };
  }>;
}

export interface AttemptResultDTO {
  score: number | null;
  started_at: string | null;
  completed_at: string | null;
  status: 'STARTED' | 'COMPLETED' | 'EXPIRED' | 'CANCELED' | null;
  provider_attempt_id: string | null;
}

export interface AttemptWindowDTO {
  start_at: string | null;
  end_at: string | null;
  duration_minutes: number | null;
}

export interface AttemptSummaryDTO {
  attempt_id: number | null;
  assigned: boolean;
  taken: boolean;
  open: boolean;
  locked: boolean;
  window: AttemptWindowDTO;
  result: AttemptResultDTO | null;
}

// Optional history entries when you pass includeHistory=true
export interface AttemptLiteDTO {
  attempt_id: number;
  status: 'STARTED' | 'COMPLETED' | 'EXPIRED' | 'CANCELED';
  score: number | null;
  started_at: string;
  completed_at: string | null;
  expires_at: string;
  provider_attempt_id: string | null;
}

export interface TraineeItemDTO {
  trainee: {
    trainee_id: number;
    name: string;
    phone: string;
    email: string;
  };
  attendance_percentage: number;
  pre: AttemptSummaryDTO;
  post: AttemptSummaryDTO;
  // present only if includeHistory=true
  pre_history?: AttemptLiteDTO[];
  post_history?: AttemptLiteDTO[];
}

export interface AttendanceSegmentSummaryDTO {
  scheduled: number;
  recorded: number;
  missing: string[];
}

export interface PostUnlockAttendanceSummaryDTO {
  complete: boolean;
  totals: {
    scheduled: number;
    recorded: number;
    missing: string[];
  };
  segments: Record<'launchpad' | 'basecamp' | 'domain' | 'ojt', AttendanceSegmentSummaryDTO>;
  recordedDates: string[];
  scheduledDates: string[];
}

export interface PostUnlockStatusResponse {
  batch_id: number;
  planned_datetime: string | null;
  actual_datetime: string | null;
  end_datetime: string | null;
  can_ca_mp_edit: boolean;
  attendance: PostUnlockAttendanceSummaryDTO;
  allowed_for_user: boolean;
  blocked_reason: string | null;
  updated_by: { user_id: string; full_name: string | null } | null;
  updated_at: string | null;
}

export interface UpdatePostUnlockPayload {
  scheduled_datetime?: string | null;
}

export interface BatchKPIsDTO {
  conversion: {
    pre: { assigned: number; taken: number; completionRatePct: number };
    post: { assigned: number; taken: number; completionRatePct: number };
    readyForPost: number;
  };
  windows: {
    pre: { isActive: boolean; opensInMs: number; closesInMs: number; openCount: number; expiredLockedCount: number; startedButOverWindow: number };
    post: { isActive: boolean; opensInMs: number; closesInMs: number; openCount: number; expiredLockedCount: number; startedButOverWindow: number };
  };
  performance: {
    pre: { avgScore: number | null; medianScore: number | null };
    post: { avgScore: number | null; medianScore: number | null };
    improvement: { avgDelta: number | null; medianDelta: number | null };
  };
  attendance: {
    averagePct: number;
    belowThresholdCount: number;
    thresholdPct: number;
  };
  integrity: {
    ipMismatchCount: number;
    uaMismatchCount: number;
  };
}

export interface BatchRowDTO {
  batch_id: number;
  batch_code: string;
  status: 'Draft' | 'InProgress' | 'Completed' | 'Cancelled';
  program_name: string | null;
  center_name: string | null;
  mp_name: string | null;
  ca_name: string | null;
  trainer_name: string | null;
  trainee_count: number;
  kpis: BatchKPIsDTO;
  trainees: TraineeItemDTO[];
}

export interface AdminListResponse {
  total: number;
  page: number;
  pageSize: number;
  rows: BatchRowDTO[];
}

export interface AdminQuery {
  q?: string;
  batchStatus?: string;
  onlyOpen?: boolean;
  page?: number;
  pageSize?: number;
  sort?: 'recent' | 'batch' | 'trainee';
  // optional extras your API supports
  includeHistory?: boolean;
  historyLimit?: number;
  lowAttendanceThreshold?: number;
}
export const assessmnetsApi = {

  getTraineeAssessments: async (): Promise<ApiResponse<TraineeAssessmentsDTO>> => {
    const resp = await api.get<ApiResponse<TraineeAssessmentsDTO>>('/trainee-assessments');
    return resp.data;
  },

  startAssessment: async (batchId: number, type: Side): Promise<ApiResponse<{ launch_url: string; expires_at: string }>> => {
    const resp = await api.post<ApiResponse<{ launch_url: string; expires_at: string }>>(
      `/assessments/${batchId}/${type}/start`
    );
    return resp.data;
  },

  getAdminAssessments: async (query: AdminQuery): Promise<ApiResponse<AdminListResponse>> => {
    const resposne = await api.get<ApiResponse<AdminListResponse>>('/admin/assessments', { params: query });
    return resposne.data;
  },

  exportAdminAssessments: async (query: AdminQuery): Promise<any> => {
    const res = await api.get<any>('/api/admin/assessments/export', { params: query, responseType: 'blob' });
    return { status: res.status, data: new Blob([res.data], { type: 'text/csv' }) };
  },

  getPostUnlockStatus: async (batchId: number): Promise<ApiResponse<PostUnlockStatusResponse>> => {
    const response = await api.get<ApiResponse<PostUnlockStatusResponse>>(
      `/assessments/batches/${batchId}/post-unlock-status`
    );
    return response.data;
  },

  updatePostUnlock: async (
    batchId: number,
    payload: UpdatePostUnlockPayload
  ): Promise<ApiResponse<PostUnlockStatusResponse>> => {
    const response = await api.post<ApiResponse<PostUnlockStatusResponse>>(
      `/assessments/batches/${batchId}/post-unlock`,
      payload
    );
    return response.data;
  },

}

export interface CertBatchRow {
  batch_id: number;
  batch_code: string;
  status: 'InProgress' | 'InPlacement' | 'Completed' ;
  program: string;
  program_code: string;
  center: string;
  mp: string;
  trainer: string;
  eligible_to_generate: boolean;
  totals: { enrolled: number; generated: number; failed: number; published: number };
  post_assessment_end_datetime: string;
}

export interface EligibilityCriterion {
  value: number | null;
  threshold: number;
  passed: boolean;
  max?: number | null;
  unit?: string;
  note?: string | null;
  raw?: {
    value: number | null;
    max?: number | null;
  };
}

export interface CertificateEligibility {
  eligible: boolean;
  reason: string | null;
  criteria: {
    attendance: EligibilityCriterion;
    post: EligibilityCriterion;
    ojt: EligibilityCriterion;
    weighted: EligibilityCriterion;
  };
}

export interface CertTraineeRow {
  trainee_id: number;
  name: string;
  email: string | null;
  eligibility: CertificateEligibility;
  cert: {
    certificate_id?: number;
    gen_status: 'NOT_STARTED' | 'QUEUED' | 'GENERATING' | 'GENERATED' | 'FAILED';
    gen_attempts: number;
    is_published: boolean;
    published_at?: string | null;
    serial_no?: string | null;
    certificate_url?: string | null; // only admin
    gen_error?: string | null;
  }
}
export interface PlacementOverviewRow {
  placement_id: number;
  batch_id: number;
  batch_code: string | null;
  batch_status: string | null;
  program_name: string | null;
  program_code: string | null;
  center_name: string | null;
  mp_name: string | null;
  trainee_id: number;
  trainee_name: string | null;
  trainee_email: string | null;
  company_name: string | null;
  job_role: string | null;
  offered_salary: string | null;
  job_type: string | null;
  recorded_by: string | null;
  recorded_on: string | null;
  metadata: any;
}

export interface PlacementBatchSummary {
  batch_id: number;
  batch_code: string;
  status: string;
  program_name: string;
  center_name: string;
  mobilisation_partner_name: string;
  center_admin_name: string;
  total_trainees: number;
  eligible_trainees: number;
  placed_trainees: number;
  pending_placements: number;
  last_recorded_on: string | null;
  post_assessment_completed: boolean;
  post_assessment_end_datetime: string | null;
  ready_for_placements: boolean;
  can_edit: boolean;
  can_edit_tracking?: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface PlacementTraineePlacement {
  placement_id: number;
  company_name: string;
  job_role: string;
  job_type: string;
  job_type_label: string;
  offered_salary: string;
  offered_salary_value: number | null;
  recorded_by: string;
  recorded_on: string | null;
  metadata: any;
   workflow_status: 'Draft' | 'Submitted' | 'Verified';
   submitted_by: string | null;
   submitted_on: string | null;
   verified_by: string | null;
   verified_on: string | null;
}

export interface PostPlacementCallTemplate {
  slot: string;
  order: number;
  label: string;
}

export interface PostPlacementCall {
  call_id: number;
  slot: string;
  slot_label: string;
  sequence_order: number;
  remark: string;
  call_made_on: string | null;
  updated_at: string | null;
  updated_by: {
    user_id: string;
    full_name: string | null;
    email: string | null;
    role: string | null;
  } | null;
}

export interface PostPlacementTracking {
  tracking_id: number;
  created_at: string;
  updated_at: string | null;
  calls: PostPlacementCall[];
}

export interface PlacementManageTrainee {
  trainee_id: number;
  name: string;
  email: string;
  phone: string;
  post_assessment_done: boolean;
  post_assessment_status: 'COMPLETED' | 'NOT_COMPLETED';
  has_placement: boolean;
  placement: PlacementTraineePlacement | null;
  tracking: PostPlacementTracking | null;
  tracking_editable: boolean;
}

export interface PlacementBatchDetail {
  batch: PlacementBatchSummary & { trainer_name: string };
  tracking_templates: PostPlacementCallTemplate[];
  trainees: PlacementManageTrainee[];
}

export interface PlacementBulkUpsertPayload {
  upserts?: Array<{
    trainee_id: number;
    company_name?: string | null;
    job_role?: string | null;
    offered_salary?: string | number | null;
    job_type?: string | null;
    job_type_label?: string | null;
    job_type_display?: string | null;
    job_type_key?: string | null;
    metadata?: any;
  }>;
  deletes?: number[];
}

export const placementsApi = {
  listBatchesReady: () =>
    api.get<ApiResponse<{ batches: PlacementBatchSummary[] }>>('/placements/batches-ready'),

  listManageBatches: () =>
    api.get<ApiResponse<{ batches: PlacementBatchSummary[] }>>('/placements/manage/batches'),

  getBatchDetail: (batchId: number) =>
    api.get<ApiResponse<PlacementBatchDetail>>(`/placements/batch/${batchId}/trainees`),

  getBatchTrainees: (batchId: number) =>
    api.get<ApiResponse<PlacementBatchDetail>>(`/placements/batch/${batchId}/trainees`),

  saveBatchPlacements: (batchId: number, payload: PlacementBulkUpsertPayload) =>
    api.post<ApiResponse<PlacementBatchDetail>>(`/placements/batch/${batchId}/bulk-upsert`, payload),

  bulkUpsertPlacements: (batchId: number, payload: PlacementBulkUpsertPayload) =>
    api.post<ApiResponse<PlacementBatchDetail>>(`/placements/batch/${batchId}/bulk-upsert`, payload),

  submitBatchPlacements: (batchId: number) =>
    api.post<ApiResponse<PlacementBatchDetail>>(`/placements/batch/${batchId}/submit`),

  verifyPlacement: (placementId: number, verified: boolean) =>
    api.post<ApiResponse<PlacementBatchDetail>>(`/placements/${placementId}/verify`, { verified }),

  listMine: () => api.get<ApiResponse<{ rows: PlacementOverviewRow[] }>>('/placements/mine'),

  exportCsv: (batchId: number | string) =>
    api.get(`/placements/batch/${batchId}/export`, { responseType: 'blob' }),

  updateTrackingCall: (callId: number, payload: { remark?: string; call_made_on?: string | null }) =>
    api.patch<ApiResponse<{ tracking: PostPlacementTracking }>>(
      `/placements/post-placement-calls/${callId}`,
      payload
    ),

  exportTrackingWorkbook: (batchId: number | string) =>
    api.get(`/placements/batch/${batchId}/post-placement/export`, { responseType: 'blob' }),

  markPostPlacement: (batchId: number | string) =>
    api.post<ApiResponse<PlacementBatchDetail>>(`/placements/batch/${batchId}/mark-post-placement`),
};

export const inquiriesApi = {
  list: async (params?: { page?: number; limit?: number; status?: InquiryStatus }): Promise<ApiResponse<InquirySummary[]>> => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.status) query.append('status', params.status);
    const qs = query.toString();
    const response = await api.get<ApiResponse<InquirySummary[]>>(`/inquiries${qs ? `?${qs}` : ""}`);
    return response.data;
  },

  get: async (inquiryId: number): Promise<ApiResponse<InquiryDetail>> => {
    const response = await api.get<ApiResponse<InquiryDetail>>(`/inquiries/${inquiryId}`);
    return response.data;
  },

  create: async (payload: { message: string }): Promise<ApiResponse<InquirySummary>> => {
    const response = await api.post<ApiResponse<InquirySummary>>('/inquiries', payload);
    return response.data;
  },

  addMessage: async (inquiryId: number, payload: { message: string }): Promise<ApiResponse<InquiryMessage>> => {
    const response = await api.post<ApiResponse<InquiryMessage>>(`/inquiries/${inquiryId}/messages`, payload);
    return response.data;
  },

  updateStatus: async (inquiryId: number, payload: { status: InquiryStatus; note?: string }): Promise<ApiResponse<InquiryMessage>> => {
    const response = await api.patch<ApiResponse<InquiryMessage>>(`/inquiries/${inquiryId}/status`, payload);
    return response.data;
  },
};

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_username: string | null;
  actor_role: string | null;
  http_method: string;
  url: string;
  status_code: number;
  source_ip: string | null;
}

export const auditApi = {
  getAuditLogs: async (params?: {
    page?: number;
    limit?: number;
    method?: string;
    status?: number;
    url?: string;
    actor?: string;
    role?: string;
  }): Promise<ApiResponse<AuditLogEntry[]>> => {
    const response = await api.get<ApiResponse<AuditLogEntry[]>>('/audit-logs', {
      params: {
        page: params?.page,
        limit: params?.limit,
        method: params?.method,
        status: params?.status,
        url: params?.url,
        actor: params?.actor,
        role: params?.role,
      },
    });
    return response.data;
  },
};

export interface InvoiceBatchLink {
  batch_id: number;
  batch_code: string | null;
}

export interface InvoiceRecord {
  invoice_id: number;
  mp_id: number;
  mobilisation_partner?: { mp_id: number; name: string | null } | null;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: string;
  invoice_for?: string | null;
  document_key: string;
  document_name?: string | null;
  document_mime?: string | null;
  document_size?: number | null;
  is_paid: boolean;
  paid_at?: string | null;
  paid_by?: string | null;
  created_at: string;
  updated_at?: string | null;
  batches: InvoiceBatchLink[];
}

export interface InvoiceListResponse {
  success: boolean;
  data: InvoiceRecord[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface InvoiceDetailResponse {
  success: boolean;
  data: InvoiceRecord;
}

export interface InvoiceDocumentPayload {
  key: string;
  name?: string;
  mime?: string;
  size?: number;
}

export interface InvoiceCreatePayload {
  invoice_date: string;
  invoice_amount: number | string;
  invoice_for?: string;
  batch_ids: number[];
  document: InvoiceDocumentPayload;
}

export interface InvoiceUpdatePayload {
  invoice_date: string;
  invoice_amount: number | string;
  invoice_for?: string;
  batch_ids: number[];
  document?: InvoiceDocumentPayload | null;
}

export const invoiceApi = {
  list: async (params?: {
    page?: number;
    pageSize?: number;
    mpId?: number;
    isPaid?: boolean;
    search?: string;
    from?: string;
    to?: string;
    batch?: string;
  }): Promise<InvoiceListResponse> => {
    const response = await api.get<InvoiceListResponse>('/invoices', { params });
    return response.data;
  },

  get: async (invoiceId: number): Promise<InvoiceDetailResponse> => {
    const response = await api.get<InvoiceDetailResponse>(`/invoices/${invoiceId}`);
    return response.data;
  },

  create: async (payload: InvoiceCreatePayload): Promise<InvoiceDetailResponse> => {
    const response = await api.post<InvoiceDetailResponse>('/invoices', payload);
    return response.data;
  },

  update: async (invoiceId: number, payload: InvoiceUpdatePayload): Promise<InvoiceDetailResponse> => {
    const response = await api.put<InvoiceDetailResponse>(`/invoices/${invoiceId}`, payload);
    return response.data;
  },

  markPaid: async (invoiceId: number): Promise<InvoiceDetailResponse> => {
    const response = await api.post<InvoiceDetailResponse>(`/invoices/${invoiceId}/mark-paid`, {});
    return response.data;
  },

  listBatchOptions: async (params?: { mpId?: number }): Promise<{ success: boolean; data: InvoiceBatchLink[] }> => {
    const response = await api.get<{ success: boolean; data: InvoiceBatchLink[] }>('/invoices/batch-options', {
      params,
    });
    return response.data;
  },
};

export const ojtScorecardApi = {
  getMeta: async (): Promise<OjtScorecardMeta> => {
    const response = await api.get<ApiResponse<OjtScorecardMeta>>('/ojt-scorecards/meta');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Unable to load OJT metadata');
    }
    return response.data.data;
  },
  listBatches: async (): Promise<OjtBatchSummary[]> => {
    const response = await api.get<ApiResponse<OjtBatchSummary[]>>('/ojt-scorecards/batches');
    if (!response.data.success || !response.data.data) {
      return [];
    }
    return response.data.data;
  },
  getBatchDetail: async (batchId: number): Promise<OjtBatchDetail> => {
    const response = await api.get<ApiResponse<OjtBatchDetail>>(`/ojt-scorecards/batches/${batchId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Unable to load batch details');
    }
    return response.data.data;
  },
  downloadTemplate: async (batchId: number): Promise<Blob> => {
    const response = await api.get(`/ojt-scorecards/batches/${batchId}/template`, {
      responseType: 'blob',
    });
    return response.data;
  },
  uploadScores: async (batchId: number, file: File): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ApiResponse>(
      `/ojt-scorecards/batches/${batchId}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};

export const certificatesApi = {
  listBatches: (params: { status?: string; q?: string; page?: number; pageSize?: number }) =>
    api.get<ApiResponse<{ total: number; page: number; pageSize: number; rows: CertBatchRow[] }>>('/api/certificates/batches', { params }),

  getBatch: (batchId: number) =>
    api.get<ApiResponse<{ batch: any; trainees: CertTraineeRow[] }>>(`/api/batches/${batchId}/certificates`),

  generate: (batchId: number, trainee_ids?: number[]) =>
    api.post<ApiResponse>(`/api/batches/${batchId}/certificates/generate`, { trainee_ids }),

  publish: (batchId: number, trainee_ids?: number[]) =>
    api.post<ApiResponse>(`/api/batches/${batchId}/certificates/publish`, { trainee_ids }),

  regenerateOne: (certificateId: number) =>
    api.post<ApiResponse>(`/api/certificates/${certificateId}/regenerate`),

  donwoadlOneCert: (certificateId: number) =>
    api.get<ApiResponse>(`/api/certificates/${certificateId}/download`),

  exportCsv: (batchId: number) =>
    api.get(`/api/batches/${batchId}/certificates/export`, { responseType: 'blob' }),

  // list only my certificates (trainee)
  listMine: () => api.get('/api/certificates/mine'),

  viewOne: (certificate_id: number) => api.get(`/api/certificates/${certificate_id}/view`),

};


export const healthApi = {
  // Check health
  checkHealth: async (): Promise<any> => {
    const response = await api.get('/health');
    return response.data;
  },
};


export async function signUploadKeys(keys: string[]): Promise<Record<string, string>> {
  if (!keys?.length) return {};
  const resp = await api.post('/uploads/sign', { keys });
  return resp.data.urls as Record<string, string>;
}

export async function deleteUploadKey(key: string): Promise<void> {
  await api.delete('/uploads', { data: { key } });
}


// Export the api instance for direct use
export { api };
